import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import * as yup from 'yup';

import { AutocompleteField, TextField } from '@components/FormFields';
import { useEntitiesCreate, useProfile, useProfileUpdate } from '@hooks';
import { autocompleteEntities } from '@utils/backend-api/entities';

import StepContainer from './StepContainer';

interface StepProfileProps {
  currentUserId: number;
  nextStep: () => void;
}

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  company: { label: '', value: 0 },
  jobTitle: { label: '', value: 0 },
};

const StepProfile = ({ nextStep, currentUserId }: StepProfileProps) => {
  const [initialValues, setInitialValues] = useState(INITIAL_STATE);
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, error } = useProfileUpdate(currentUserId);
  const { profile, isLoadingProfile } = useProfile();

  const handleSubmit = async (values) => {
    const sanitizedParams = {
      name: `${values.firstName} ${values.lastName}` || '',
      company_name: values.company?.label || '',
      title_at_company: values.jobTitle?.label || '',
      profile_enabled: true,
    };
    saveProfile(sanitizedParams);
    nextStep();
  };

  useEffect(() => {
    if (!isLoadingProfile) {
      const name = profile?.name?.split(' ');
      setInitialValues({
        firstName: name[0] === 'Guest' ? '' : name[0] || '',
        lastName: name[1] || '',
        company: profile.company || { label: '', value: 0 },
        jobTitle: profile.titleAtCompany || { label: '', value: 0 },
      });
    }
  }, [isLoadingProfile, profile?.name, profile?.company, profile?.titleAtCompany]);

  if (isLoadingProfile) return null;
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={yup.object().shape({
        firstName: yup.string().required('Please enter a first name'),
        lastName: yup.string().required('Please enter a last name'),
      })}
    >
      {(formik) => (
        <StepContainer
          heading="Create Your Public Subscriber Profile"
          subheading="Join our exclusive community by adding your profile to the subscriber directory."
          onContinue={formik.handleSubmit}
          nextStep={nextStep}
          error={error}
        >
          <Form noValidate onSubmit={formik.handleSubmit} className="form-container">
            <TextField name="firstName" label="First Name" />
            <TextField name="lastName" label="Last Name" />
            <AutocompleteField
              label="Company"
              name="company"
              fetchMethod={(value) =>
                autocompleteEntities({
                  fragment: value,
                  entity_type: 'organization',
                  word_type: 'proper',
                })
              }
              createMethod={(value) =>
                createEntityAsync({
                  name: value,
                  entity_type: 'organization',
                  word_type: 'proper',
                })
              }
            />
            <AutocompleteField
              label="Job Title"
              name="jobTitle"
              fetchMethod={(value) =>
                autocompleteEntities({
                  fragment: value,
                  entity_type: 'job_title',
                })
              }
              createMethod={(value) =>
                createEntityAsync({
                  name: value,
                  entity_type: 'job_title',
                  word_type: 'common',
                })
              }
            />
          </Form>
        </StepContainer>
      )}
    </Formik>
  );
};

export default StepProfile;
